import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const isScrollable = (el: HTMLElement): boolean => {
    const { overflow, overflowY } = getComputedStyle(el);
    return [overflow, overflowY].some((v) => v === 'auto' || v === 'scroll');
};

const getScrollContainer = (): HTMLElement | Window => {
    const appContainer = document.querySelector<HTMLElement>('.app-container');

    if (appContainer) {
        // Standalone: scroll container is a descendant (e.g. AmelioWrapperMock's overflow-auto div)
        const descendant = appContainer.querySelector<HTMLElement>('#main-scroll-container');
        if (descendant) {
            return descendant;
        }

        // Legacy: scroll container is an ancestor (host app's content area)
        let ancestor = appContainer.parentElement;
        while (ancestor) {
            if (isScrollable(ancestor)) {
                return ancestor;
            }
            ancestor = ancestor.parentElement;
        }
    }

    // Embedded: the module renders inside a shadow root, which a document-wide query does not
    // reach, and the host scrolls the window itself.
    return window;
};

/**
 * Opens a new screen at the top of the page, at once: a smooth scroll would show the reader the
 * middle of the screen they just asked for before taking them to its top.
 *
 * Every change of screen goes through it -- a route, a tab of a page, a screen standing in for the
 * page -- so there is one answer to where a new screen opens.
 */
export const scrollPageToTop = () => {
    getScrollContainer().scrollTo({ top: 0 });
};

/**
 * Opens every route of the module's own router at the top of the page. A change of the query string
 * alone is a filter of the same page and keeps the reader in place, and the back and forward buttons
 * are left to the browser's scroll restoration. When the module is embedded, the host already does
 * the same for every address it hears; this covers the moves only the module's router hears.
 */
export const ScrollToTop = () => {
    const history = useHistory();
    const { pathname } = useLocation();

    useEffect(() => {
        if (history.action === 'POP') {
            return;
        }

        requestAnimationFrame(scrollPageToTop);
    }, [history, pathname]);

    return null;
};
